import { Dispatch, FunctionComponent, SetStateAction } from "react";
import {
  Button,
  Grid2,
  Typography,
  Dialog,
  Box,
  useTheme,
  useMediaQuery,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import "@lottiefiles/lottie-player";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { AffectedExistingBooking } from "../../features/Booking-Form/typings/cancel-existing-booking.types";
import { DateTime } from "luxon";
import useTimeFormat from "../../hooks/useTimeFormat/useTimeFormat";
import { BookingErrorType } from "./Domain/booking-error-desc.type";

interface OwnProps {
  showConfirmAnim: boolean;
  showErrorAnim: boolean;
  showCancelExistingConfirm: boolean;
  affectedExistingBookings: AffectedExistingBooking[];
  setOpen: Dispatch<
    SetStateAction<{
      conference: boolean;
    }>
  >;
  confirmingBooking: boolean;
  onSuccessAction?: () => void;
  onCancelExistingProceed: () => void;
  onCancelExistingDismiss: () => void;
}

type Props = OwnProps;

function formatAffectedBookingRange(
  booking: AffectedExistingBooking,
  dateTimeFormat: string
): string {
  const start = DateTime.fromISO(booking.start);
  const end = DateTime.fromISO(booking.end);
  if (start.isValid && end.isValid) {
    return `${start.toFormat(dateTimeFormat)} – ${end.toFormat(dateTimeFormat)}`;
  }
  return booking.label ?? `#${booking.id}`;
}

const FormConfirmDialog: FunctionComponent<Props> = ({
  showConfirmAnim,
  showErrorAnim,
  showCancelExistingConfirm,
  affectedExistingBookings,
  setOpen,
  confirmingBooking,
  onSuccessAction,
  onCancelExistingProceed,
  onCancelExistingDismiss
}) => {
  const { errorConfirm: errorMsg } = useSelector((state: RootState) => state.booking.inputs);
  const { t } = useTranslation();
  const theme = useTheme();
  const borderColor = theme.palette.mode === "light" ? "#ffffff" : "#4a4b53";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { dateFormat, timeFormat } = useTimeFormat();
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;

  const dialogOpen = showConfirmAnim || showErrorAnim || showCancelExistingConfirm;

  const knownBookingErrorMessages = new Set<string>(Object.values(BookingErrorType));
  let errorMessage = errorMsg;
  if (knownBookingErrorMessages.has(errorMsg ?? "")) {
    errorMessage = errorMsg;
  } else if (errorMsg?.includes("invalid_")) {
    errorMessage = t("There was a booking error");
  } else {
    errorMessage = t("There was a server error");
  }

  if (showCancelExistingConfirm) {
    return (
      <Dialog
        open={dialogOpen}
        onClose={onCancelExistingDismiss}
        aria-labelledby="cancel-existing-dialog-title"
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle id="cancel-existing-dialog-title">
          {t("_bookingConfirm_cancelExistingTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("_bookingConfirm_cancelExistingMessage")}
          </DialogContentText>
          <Table size="small" sx={{ "& td, & th": { borderBottom: 1, borderColor: "divider" } }}>
            <TableHead>
              <TableRow>
                <TableCell component="th" variant="head">
                  {t("Booking")}
                </TableCell>
                <TableCell component="th" variant="head">
                  {t("Date and Time")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {affectedExistingBookings.map(booking => (
                <TableRow key={booking.id}>
                  <TableCell>
                    {booking.label ?? t(booking.bookingType)}
                    {booking.action === "shorten"
                      ? ` (${t("_bookingConfirm_cancelExistingShorten")})`
                      : ""}
                  </TableCell>
                  <TableCell>{formatAffectedBookingRange(booking, dateTimeFormat)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onCancelExistingDismiss}
            color="secondary"
            data-testid="cancel-existing-dialog-dismiss-btn"
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={onCancelExistingProceed}
            color="primary"
            variant="contained"
            loading={confirmingBooking}
            data-testid="cancel-existing-dialog-proceed-btn"
          >
            {t("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={dialogOpen}
      onClose={() => setOpen(prevState => ({ ...prevState, menu: false }))}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      slotProps={{ paper: { sx: { borderRadius: 3, m: 0 } } }}
    >
      <Grid2
        data-testid="confirm-dialog-parent"
        container
        sx={[isMobile ? { padding: 0.5 } : { padding: 3 }]}
        alignItems={"center"}
        direction={"column"}
        justifyContent={"flex-start"}
      >
        {!confirmingBooking && (showConfirmAnim || showErrorAnim) && (
          <Grid2
            data-testid="confirm-dialog-contents"
            container
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              pb: 3,
              border: `1px solid ${borderColor}`,
              borderRadius: 3
            }}
          >
            {showConfirmAnim ? (
              <ShowBookingSuccess onSuccessAction={onSuccessAction} />
            ) : (
              <ShowBookingError
                errorMsg={errorMessage}
                setOpen={setOpen}
                onSuccessAction={onSuccessAction}
              />
            )}
          </Grid2>
        )}
      </Grid2>
    </Dialog>
  );
};

export default FormConfirmDialog;

function ShowBookingSuccess({ onSuccessAction }: Readonly<{ onSuccessAction?: () => void }>) {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const stylePadding = isMobile ? 0 : "0 32px 32px 32px";

  return (
    <>
      <Grid2 sx={{ mt: 1, mb: 2, padding: stylePadding, justifyItems: "center" }}>
        <Box sx={{ width: 200, height: 200, m: "0 auto" }}>
          <lottie-player
            mode="normal"
            style={{ width: "200px" }}
            src="https://assets9.lottiefiles.com/packages/lf20_p2qq4ikl.json"
            autoplay
          />
        </Box>

        <Box sx={{ width: 300 }}>
          <Typography align={"center"} variant={"h5"}>
            {t("Booking confirmed")}
          </Typography>
        </Box>
      </Grid2>

      <Button
        onClick={() => {
          if (onSuccessAction) {
            onSuccessAction();
            return;
          }
          history.push("/");
        }}
        data-testid="confirm-info-btn"
        sx={{ minWidth: "70%" }}
      >
        {t("Back to dashboard")}
      </Button>
    </>
  );
}

function ShowBookingError({
  errorMsg = "Booking Error",
  setOpen,
  onSuccessAction
}: Readonly<{
  errorMsg: string | undefined;
  onSuccessAction?: () => void;
  setOpen: Dispatch<
    SetStateAction<{
      conference: boolean;
    }>
  >;
}>) {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const stylePadding = isMobile ? 0 : "0 32px 32px 32px";

  return (
    <>
      <Grid2 sx={{ mt: 1, mb: 2, padding: stylePadding, justifyItems: "center" }}>
        <Box sx={{ width: 200, height: 200, m: "0 auto" }}>
          <lottie-player
            src="https://assets3.lottiefiles.com/packages/lf20_gzlupphk.json"
            mode="normal"
            style={{ width: "200px" }}
            autoplay
          />
        </Box>

        <Box sx={{ width: 300 }}>
          {errorMsg && (
            <Typography align={"center"} variant={"h5"}>
              {t(errorMsg)}
            </Typography>
          )}
        </Box>
      </Grid2>

      <Button
        onClick={() => {
          if (onSuccessAction) {
            onSuccessAction();
            return;
          }
          setOpen(prevState => ({ ...prevState, conference: false }));
          history.push("/booking/0");
        }}
        data-testid={"backButton"}
        sx={{ minWidth: "70%" }}
      >
        {t("Try again")}
      </Button>
    </>
  );
}
